/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import validator from "validator";
import * as yup from "yup";

import { SeverityLevel } from "@microsoft/applicationinsights-web";

import * as validatorHelper from "../../Utility/ValidateHelper";

import { STATE_COUNTRIES, VALIDATE_COUNTRY_NAMES } from "../../Constants/ProfileConstants";
import {
  ADDRESS_ENGLISH_REG_EX,
  EMAIL_NOT_ALLOWED,
  INPUT_REQUIRED,
  INVALID,
  INVALID_ADDRESS_ENGLISH_CHARS,
  INVALID_EMAIL_REG_EX,
  INVALID_PHONE_NUMBER,
  SELECTION_REQUIRED,
  TOO_LONG,
} from "../../Constants/ValidationMessages";
import { getAppInsights } from "../../Services/TelemetryService";

const StudentProfileValidationSchema = yup.object({
  titleName: yup.string().required(SELECTION_REQUIRED),
  firstName: yup.string().trim().required(INPUT_REQUIRED),
  lastName: yup.string().trim().required(INPUT_REQUIRED),
  genderName: yup.string().trim().required(SELECTION_REQUIRED),
  dateOfBirth: yup.date().required(SELECTION_REQUIRED),
  address1: yup
    .string()
    .max(35, TOO_LONG)
    .trim()
    .required(INPUT_REQUIRED)
    .matches(ADDRESS_ENGLISH_REG_EX, INVALID_ADDRESS_ENGLISH_CHARS),
  address2: yup.string().max(35, TOO_LONG).trim().matches(ADDRESS_ENGLISH_REG_EX, {
    message: INVALID_ADDRESS_ENGLISH_CHARS,
    excludeEmptyString: true,
  }),
  addressCity: yup
    .string()
    .max(35, TOO_LONG)
    .trim()
    .required(INPUT_REQUIRED)
    .matches(ADDRESS_ENGLISH_REG_EX, INVALID_ADDRESS_ENGLISH_CHARS),
  addressCountryOrRegionName: yup.string().trim().required(SELECTION_REQUIRED),
  addressStateOrProvince: yup
    .string()
    .trim()
    .when("addressCountryOrRegionName", (val) => {
      return STATE_COUNTRIES.indexOf(val) !== -1
        ? yup.string().required(SELECTION_REQUIRED)
        : yup.string().nullable();
    }),
  addressPostCode: yup.string().max(10, TOO_LONG).trim().required(INPUT_REQUIRED),

  shippingAddress1: yup.string().when("shippingSameAsAddress", {
    is: false,
    then: yup
      .string()
      .max(35, TOO_LONG)
      .trim()
      .required(INPUT_REQUIRED)
      .matches(ADDRESS_ENGLISH_REG_EX, INVALID_ADDRESS_ENGLISH_CHARS),
    otherwise: yup
      .string()
      .max(35, TOO_LONG)
      .trim()
      .matches(ADDRESS_ENGLISH_REG_EX, INVALID_ADDRESS_ENGLISH_CHARS),
  }),
  shippingAddress2: yup.string().when("shippingSameAsAddress", {
    is: false,
    then: yup.string().max(35, TOO_LONG).trim().ensure().matches(ADDRESS_ENGLISH_REG_EX, {
      message: INVALID_ADDRESS_ENGLISH_CHARS,
      excludeEmptyString: true,
    }),
    otherwise: yup.string().max(35, TOO_LONG).trim().ensure().matches(ADDRESS_ENGLISH_REG_EX, {
      message: INVALID_ADDRESS_ENGLISH_CHARS,
      excludeEmptyString: true,
    }),
  }),
  shippingCity: yup.string().when("shippingSameAsAddress", {
    is: false,
    then: yup
      .string()
      .max(35, TOO_LONG)
      .trim()
      .required(INPUT_REQUIRED)
      .matches(ADDRESS_ENGLISH_REG_EX, INVALID_ADDRESS_ENGLISH_CHARS),
    otherwise: yup
      .string()
      .max(35, TOO_LONG)
      .trim()
      .matches(ADDRESS_ENGLISH_REG_EX, INVALID_ADDRESS_ENGLISH_CHARS),
  }),
  shippingStateOrProvince: yup
    .string()
    .trim()
    .when("shippingCountryOrRegionName", (val) => {
      return STATE_COUNTRIES.indexOf(val) !== -1
        ? yup.string().required(SELECTION_REQUIRED)
        : yup.string().nullable();
    }),
  shippingCountryOrRegionName: yup.string().when("shippingSameAsAddress", {
    is: false,
    then: yup.string().required(SELECTION_REQUIRED),
  }),
  shippingPostCode: yup.string().when("shippingSameAsAddress", {
    is: false,
    then: yup.string().max(10, TOO_LONG).trim().required(INPUT_REQUIRED),
    otherwise: yup.string().max(10, TOO_LONG).trim(),
  }),

  primaryEmail: yup.string().email(INVALID).trim().required(INPUT_REQUIRED),
  secondaryEmail: yup
    .string()
    .email(INVALID)
    .trim()
    .matches(INVALID_EMAIL_REG_EX, EMAIL_NOT_ALLOWED),
  primaryPhoneCountryOrRegionTapiCode: yup.string(),
  primaryPhone: yup
    .string()
    .nullable()
    .test("primaryPhoneNumberValidation", INVALID_PHONE_NUMBER, function (value) {
      const { path, parent, createError } = this;
      const tapiCode = parent.primaryPhoneCountryOrRegionTapiCode || "";
      const countryName = parent.primaryPhoneCountryOrRegionName || "";
      const phoneNumber = value || "";

      if (phoneNumber.length < 1) {
        return true;
      }

      const fullNumber = `+${tapiCode}${phoneNumber}`;

      // /^\d+$/ validate the phone number  of the "missing" countries.
      if (VALIDATE_COUNTRY_NAMES.indexOf(countryName) !== -1) {
        return validator.isMobilePhone(fullNumber) || validatorHelper.isMobilePhone(fullNumber);
      } else {
        if (/^\d+$/.test(fullNumber.substring(1))) {
          getAppInsights()?.trackTrace({
            message:
              "[MSALLogging]: 'missing' country " + `${countryName}+${tapiCode}+${phoneNumber}`,
            severityLevel: SeverityLevel.Information,
            properties: {},
          });
          return true;
        } else {
          return createError({
            path,
            message: `${INVALID_PHONE_NUMBER}${fullNumber}`,
          });
        }
      }
    })
    .required(INPUT_REQUIRED),
  shippingPhoneCountryOrRegionTapiCode: yup.string(),
  shippingPhoneNumber: yup
    .string()
    .nullable()
    // TODO: figure out how to implement yup.addMethod and still get access to the this context, to remove duplication.
    .when("shippingSameAsAddress", {
      is: false,
      then: yup
        .string()
        .nullable()
        .test("shippingPhoneNumberValidation", INVALID_PHONE_NUMBER, function (value) {
          const { path, parent, createError } = this;
          const tapiCode = parent.shippingPhoneCountryOrRegionTapiCode || "";
          const countryName = parent.shippingPhoneCountryOrRegionName || "";
          const phoneNumber = value || "";

          if (phoneNumber.length < 1) {
            return true;
          }

          const fullNumber = `+${tapiCode}${phoneNumber}`;

          // /^\d+$/ validate the phone number  of the "missing" countries.
          if (VALIDATE_COUNTRY_NAMES.indexOf(countryName) !== -1) {
            return validator.isMobilePhone(fullNumber) || validatorHelper.isMobilePhone(fullNumber);
          } else {
            if (/^\d+$/.test(fullNumber.substring(1))) {
              getAppInsights()?.trackTrace({
                message:
                  "[MSALLogging]: 'missing' country " + `${countryName}+${tapiCode}+${phoneNumber}`,
                severityLevel: SeverityLevel.Information,
                properties: {},
              });
              return true;
            } else {
              return createError({
                path,
                message: `${INVALID_PHONE_NUMBER}${fullNumber}`,
              });
            }
          }
        })
        .required(INPUT_REQUIRED),
    }),
  userProfileSchool: yup.array().of(
    yup.object().shape({
      schoolName: yup.string().ensure().trim().required(INPUT_REQUIRED),
      schoolDegreeLevelName: yup.string().trim().required(SELECTION_REQUIRED),
      schoolDegreeProgramName: yup.string().trim().required(SELECTION_REQUIRED),
      schoolCountryRegionName: yup.string().trim().required(SELECTION_REQUIRED),
      expectedGraduationDate: yup.date().required(SELECTION_REQUIRED),
      schoolStateProvinceName: yup
        .string()
        .trim()
        .when("schoolCountryRegionName", (val) => {
          return STATE_COUNTRIES.indexOf(val) !== -1
            ? yup.string().required(SELECTION_REQUIRED)
            : yup.string().nullable();
        }),
    })
  ),

  companyName: yup.string().trim().ensure().trim(),
  companyRole: yup.string().ensure().trim(),
});

export default StudentProfileValidationSchema;
